import { useMemo, useState } from "react";
import ArrowForward from "@mui/icons-material/ArrowForward";
import ArrowOutward from "@mui/icons-material/ArrowOutward";
import DarkModeOutlined from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlined from "@mui/icons-material/LightModeOutlined";
import { useTheme } from "../contexts/ThemeContext";
import type { BlogPost } from "../types/blog";
import SubscribeForm from "./SubscribeForm";
import "./ReadingRoomHome.css";

interface ReadingRoomHomeProps {
  posts: BlogPost[];
  isLoading: boolean;
  isLoggedIn: boolean;
  onSignOut: () => Promise<void>;
}

export default function ReadingRoomHome({
  posts,
  isLoading,
  isLoggedIn,
  onSignOut,
}: ReadingRoomHomeProps) {
  const { theme, toggleTheme } = useTheme();
  const [visibleCount, setVisibleCount] = useState(3);
  const [featuredPost, ...archivePosts] = posts;
  const visiblePosts = archivePosts.slice(0, visibleCount);
  const featuredPreview = useMemo(() => {
    // Supplement short excerpts with the essay's own opening paragraph.
    if (!featuredPost || featuredPost.excerpt.length >= 80) return "";
    const content = new DOMParser().parseFromString(featuredPost.content, "text/html");
    return content.querySelector("p")?.textContent?.trim() || "";
  }, [featuredPost]);

  return (
    <div className="reading-room">
      <a className="reading-room-skip" href="#writing">Skip to writing</a>

      <aside className="reading-room-sidebar" aria-label="About the author">
        <div className="reading-room-profile">
          <p className="reading-room-eyebrow">Personal essays</p>
          <img
            className="reading-room-portrait"
            src="/images/about-kc.png"
            alt="Pencil portrait of Arvind KC"
            width={1126}
            height={1397}
          />
          <div className="reading-room-bio">
            <a href="/" className="reading-room-name">Arvind KC</a>
            <div className="reading-room-short-rule" aria-hidden="true" />
            <p className="reading-room-manifesto">
              Notes on work, attention, and a considered life.
            </p>
            <p className="mt-6 hidden text-base leading-[1.5] sm:block sm:text-lg">
              Leadership, identity, health, technology, and the choices that compound.
            </p>
            <a href="/about" className="reading-room-link mt-6 inline-flex items-center gap-1">
              About me <ArrowOutward fontSize="inherit" />
            </a>
          </div>
        </div>
        <div className="reading-room-subscribe hidden lg:block">
          <SubscribeForm compact />
        </div>
      </aside>

      <div className="reading-room-content">
        <nav aria-label="Main navigation" className="reading-room-nav">
          <a href="/" aria-current="page" className="reading-room-link">Home</a>
          <a href="/about" className="hover:text-primary-700 dark:hover:text-primary-300">About</a>
          {isLoggedIn && (
            <>
              <a href="/secret-admin-path" className="hover:text-primary-700 dark:hover:text-primary-300">Admin</a>
              <button type="button" onClick={onSignOut}>Sign out</button>
            </>
          )}
          <button
            type="button"
            onClick={toggleTheme}
            className="reading-room-theme"
            aria-label={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
          >
            {theme === "light" ? <DarkModeOutlined fontSize="small" /> : <LightModeOutlined fontSize="small" />}
          </button>
        </nav>

        <main id="writing" tabIndex={-1} className="min-w-0 outline-none">
          <header className="reading-room-intro">
            <h1>A little space to think.</h1>
            <p>Essays for a more intentional, interesting life.</p>
          </header>

          {isLoading ? (
            <p role="status" className="py-20 text-neutral-500 dark:text-neutral-400">Opening the reading room…</p>
          ) : featuredPost ? (
            <>
              <section aria-labelledby="featured-label" className="reading-room-feature">
                <h2 id="featured-label" className="reading-room-eyebrow text-primary-700 dark:text-primary-300">Featured essay</h2>
                <div className="reading-room-feature-grid">
                  <div className="min-w-0">
                    <h3 className="reading-room-feature-title">
                      <a href={`/post/${featuredPost.id}`} className="hover:text-primary-700 dark:hover:text-primary-300">{featuredPost.title}</a>
                    </h3>
                    <p className="reading-room-feature-excerpt">{featuredPost.excerpt}</p>
                    {featuredPreview && <p className="mt-6 line-clamp-3 text-base leading-relaxed">{featuredPreview}</p>}
                    <time dateTime={featuredPost.date} className="mt-4 block text-sm text-neutral-500 dark:text-neutral-400">
                      {new Date(featuredPost.date).toLocaleDateString("en-US", {
                        month: "long", day: "numeric", year: "numeric", timeZone: "UTC",
                      })}
                    </time>
                    <a href={`/post/${featuredPost.id}`} className="reading-room-read mt-5 inline-flex items-center gap-2">
                      Read essay <ArrowOutward fontSize="small" />
                    </a>
                  </div>
                  <a href={`/post/${featuredPost.id}`} className="reading-room-feature-art" aria-label={`Read ${featuredPost.title}`}>
                    <img src={featuredPost.imageUrl} alt={featuredPost.title} width={640} height={480} />
                  </a>
                </div>
              </section>

              {archivePosts.length > 0 && (
                <section className="reading-room-archive" aria-labelledby="archive-heading">
                  <h2 id="archive-heading" className="reading-room-eyebrow">Selected writing</h2>
                  <div id="essay-archive">
                    {visiblePosts.map((post) => (
                      <article key={post.id} className="reading-room-essay">
                        <a href={`/post/${post.id}`} className="group flex items-center justify-between gap-6 py-[18px]">
                          <div className="min-w-0">
                            <h3 className="reading-room-essay-title group-hover:text-primary-700 dark:group-hover:text-primary-300">{post.title}</h3>
                            <p className="mt-1 text-base leading-relaxed text-neutral-600 dark:text-neutral-400 sm:text-lg">{post.excerpt}</p>
                          </div>
                          <ArrowForward className="shrink-0 transition-transform group-hover:translate-x-1" />
                        </a>
                      </article>
                    ))}
                  </div>
                  {visibleCount < archivePosts.length && (
                    <button
                      type="button"
                      className="reading-room-link mt-6 inline-flex min-h-11 items-center gap-2"
                      aria-controls="essay-archive"
                      onClick={() => setVisibleCount((count) => count + 6)}
                    >
                      {visibleCount === 3 ? "Explore the archive" : "More essays"} <ArrowForward fontSize="small" />
                    </button>
                  )}
                  <p role="status" className="sr-only">Showing {visiblePosts.length} of {archivePosts.length} essays in the archive.</p>
                </section>
              )}
            </>
          ) : (
            <section className="py-20">
              <h2 className="font-serif text-3xl">A new chapter is on its way.</h2>
              <p className="mt-4 text-neutral-600 dark:text-neutral-400">Subscribe to hear when the next essay is published.</p>
            </section>
          )}
        </main>
        <div className="reading-room-subscribe lg:hidden">
          <SubscribeForm compact />
        </div>
        <footer className="reading-room-footer">
          The views and opinions expressed here are my own and do not represent any organization I am or was affiliated with.
        </footer>
      </div>
    </div>
  );
}
